import { useEffect, useMemo, useRef, useState } from "react";
import {
  MessageSquare,
  Send,
  Square,
  Sparkles,
  RotateCcw,
  Copy,
  Check,
  Bot,
  User,
} from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";
import Markdown from "../components/Markdown.jsx";
import TaskBanner from "../components/TaskBanner.jsx";
import GenerationProgress from "../components/GenerationProgress.jsx";
import SpeechToTextButton, {
  appendTranscript,
} from "../components/SpeechToTextButton.jsx";
import { chatStream, getModels } from "../lib/api.js";
import { usePendingTask, useHomeTaskDone } from "../hooks/usePendingTask.js";
import { resolvePromptText } from "../lib/promptText.js";
import { useI18n } from "../lib/i18n.jsx";

const DEFAULT_SYSTEM =
  "You are The Consensus A.I., a helpful, candid, expert assistant. Answer concisely and use markdown when helpful.";

export default function Chat() {
  const { t } = useI18n();
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("nvidia/nemotron-3.5-lightning-30b-a3b");
  const [system, setSystem] = useState(DEFAULT_SYSTEM);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [taskBanner, setTaskBanner] = useState(null);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    getModels()
      .then((m) => setModels(m.chat || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, loading]);

  const send = async (textOverride) => {
    const trimmed = resolvePromptText(textOverride, input);
    if (!trimmed || loading) return;
    setError(null);

    const next = [...messages, { role: "user", content: trimmed }];
    setMessages([...next, { role: "assistant", content: "" }]);
    setInput("");
    setLoading(true);
    setStatus(t("common.starting", "Starting…"));

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const payloadMessages = [
        { role: "system", content: system },
        ...next.map((m) => ({ role: m.role, content: m.content })),
      ];
      await chatStream(
        { model, messages: payloadMessages, temperature: 0.6, max_tokens: 2048 },
        {
          signal: controller.signal,
          onStatus: setStatus,
          onDelta: (_delta, full) => {
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = { role: "assistant", content: full };
              return copy;
            });
          },
        }
      );
    } catch (e) {
      if (e.name !== "AbortError") {
        setError(e.message);
        setMessages((prev) => {
          const copy = [...prev];
          // Remove the empty assistant placeholder if it has nothing
          if (
            copy.length &&
            copy[copy.length - 1].role === "assistant" &&
            !copy[copy.length - 1].content
          ) {
            copy.pop();
          }
          return copy;
        });
      }
    } finally {
      setLoading(false);
      setStatus("");
      abortRef.current = null;
    }
  };

  const stop = () => {
    abortRef.current?.abort();
  };

  const reset = () => {
    setMessages([]);
    setError(null);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  usePendingTask("/chat", (task) => {
    setTaskBanner(task.rawPrompt || task.prompt);
    setInput(task.prompt);
    send(task.prompt);
  });

  const taskDone = useHomeTaskDone(loading, error, Boolean(taskBanner));

  const isEmpty = messages.length === 0;

  return (
    <div className="flex flex-col h-screen md:h-screen">
      <PageHeader
        icon={MessageSquare}
        title={t("nav.chat", "AI Chat")}
        subtitle={t(
          "chat.subtitle",
          "Stream responses from frontier open models via NVIDIA NIM."
        )}
        right={
          <div className="flex items-center gap-2">
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="input !w-auto max-w-[280px]"
            >
              {models.length === 0 && <option value={model}>{model}</option>}
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
            <button
              className="btn-ghost"
              onClick={reset}
              disabled={loading || isEmpty}
              title={t("chat.clear", "Clear conversation")}
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>
        }
      />

      <TaskBanner
        label={t("nav.chat", "AI Chat")}
        prompt={taskBanner}
        running={loading}
        error={error}
        completed={taskDone}
      />

      <GenerationProgress
        active={loading}
        message={status}
        className="mx-4 md:mx-10 mt-3"
      />

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-10 py-6">
        <div className="max-w-3xl mx-auto space-y-6">
          {isEmpty && <EmptyState onPick={(p) => setInput(p)} t={t} />}
          {messages.map((m, i) => (
            <Bubble
              key={i}
              role={m.role}
              content={m.content}
              t={t}
              loading={
                loading &&
                i === messages.length - 1 &&
                m.role === "assistant" &&
                !m.content
              }
            />
          ))}
          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-200 text-sm px-4 py-3">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-white/5 bg-ink-900/60 backdrop-blur p-4 md:p-6">
        <div className="max-w-3xl mx-auto">
          <div className="relative">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t(
                "chat.placeholder",
                "Message The Consensus A.I. — Shift+Enter for newline"
              )}
              rows={1}
              className="textarea pr-24 pl-20 min-h-[56px] max-h-[240px]"
            />
            <div className="absolute left-2 bottom-2 flex items-center gap-1">
              <SpeechToTextButton
                onTranscript={(text) =>
                  setInput((prev) => appendTranscript(prev, text))
                }
              />
            </div>
            <div className="absolute right-2 bottom-2 flex items-center gap-2">
              {loading ? (
                <button onClick={stop} className="btn-secondary px-3 py-2">
                  <Square className="h-4 w-4 fill-current" />
                  {t("common.stop", "Stop")}
                </button>
              ) : (
                <button
                  onClick={() => send()}
                  disabled={!input.trim()}
                  className="btn-primary px-3 py-2"
                >
                  <Send className="h-4 w-4" />
                  {t("common.send", "Send")}
                </button>
              )}
            </div>
          </div>
          <details className="mt-3 text-xs text-white/40">
            <summary className="cursor-pointer hover:text-white/70">
              {t("chat.systemPrompt", "System prompt")}
            </summary>
            <textarea
              value={system}
              onChange={(e) => setSystem(e.target.value)}
              rows={2}
              className="textarea mt-2 text-xs"
            />
            <div className="mt-2">
              <SpeechToTextButton
                onTranscript={(text) =>
                  setSystem((prev) => appendTranscript(prev, text))
                }
              />
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}

function Bubble({ role, content, loading, t }) {
  const isUser = role === "user";
  const [copied, setCopied] = useState(false);

  const copyMessage = async () => {
    if (!content?.trim()) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // Clipboard permissions can fail in some embedded browsers.
    }
  };

  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : ""}`}>
      {!isUser && (
        <div className="h-8 w-8 shrink-0 rounded-lg bg-consensus-500/15 border border-consensus-500/30 text-consensus-300 flex items-center justify-center">
          <Bot className="h-4 w-4" />
        </div>
      )}
      <div
        className={[
          "relative max-w-[85%] rounded-2xl px-4 py-3",
          isUser
            ? "bg-consensus-500 text-ink-950 rounded-tr-sm"
            : "bg-ink-800/80 border border-white/5 rounded-tl-sm",
        ].join(" ")}
      >
        {!loading && Boolean(content?.trim()) && (
          <button
            onClick={copyMessage}
            className={[
              "absolute top-2 right-2 h-7 w-7 rounded-md border flex items-center justify-center transition",
              isUser
                ? "border-ink-900/20 bg-white/30 text-ink-900 hover:bg-white/50"
                : "border-white/10 bg-white/5 text-white/70 hover:text-white hover:bg-white/10",
            ].join(" ")}
            title={
              copied
                ? t("home.share.copied", "Copied")
                : t("chat.copyMessage", "Copy message")
            }
            aria-label={
              copied
                ? t("home.share.copied", "Copied")
                : t("chat.copyMessage", "Copy message")
            }
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        )}
        {isUser ? (
          <div className="whitespace-pre-wrap text-sm font-medium pr-8">{content}</div>
        ) : loading ? (
          <TypingDots />
        ) : (
          <div className="pr-8">
            <Markdown>{content}</Markdown>
          </div>
        )}
      </div>
      {isUser && (
        <div className="h-8 w-8 shrink-0 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
          <User className="h-4 w-4" />
        </div>
      )}
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-2 w-2 rounded-full bg-consensus-400 animate-pulse-slow"
          style={{ animationDelay: `${i * 150}ms` }}
        />
      ))}
    </div>
  );
}

const SUGGESTIONS = [
  "Explain how a neural network learns, like I'm 12.",
  "Write a Python script that downloads YouTube transcripts.",
  "Compare Llama 3.3 70B vs GPT-4 for coding tasks.",
  "Draft a launch tweet for 'The Consensus A.I.'.",
];

function EmptyState({ onPick, t }) {
  return (
    <div className="text-center py-12">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-consensus-500/10 border border-consensus-500/30 text-consensus-300 shadow-glow mb-5">
        <Sparkles className="h-7 w-7" />
      </div>
      <h2 className="text-2xl font-bold tracking-tight">
        {t("chat.emptyTitle", "Ask The Consensus anything")}
      </h2>
      <p className="text-white/50 mt-2 max-w-md mx-auto text-sm">
        {t(
          "chat.emptySubtitle",
          "Open-weight frontier models, streamed live through NVIDIA NIM."
        )}
      </p>
      <div className="mt-8 grid sm:grid-cols-2 gap-2 max-w-2xl mx-auto text-left">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            className="rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 transition p-3 text-sm text-white/80"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
